import { useEffect, useRef, useState } from 'react';
import { getBrands } from '../services/Api';

export const useBrands = () => {
  const [allBrands, setAllBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const abortControllerRef = useRef(null);

  // Add "No Brand" option to brands array
  const addNoBrandOption = (brands) => {
    const noBrandOption = { 
      name: 'No Brand', 
      brand_id: null,
      id: null 
    };
    return [noBrandOption, ...brands];
  };

  // Fetch initial brands when component mounts
  useEffect(() => {
    if (!hasFetched) {
      fetchInitialBrands();
    }
  }, []);

  const fetchInitialBrands = async () => {
    if (hasFetched) return;

    setLoading(true);
    try {
      const response = await getBrands({
        name: "",
        paginate_per_page: 10
      });

      const brands = response?.data || [];
      // Add "No Brand" option to the beginning
      const brandsWithNoBrand = addNoBrandOption(brands);
      
      setAllBrands(brandsWithNoBrand);
      setFilteredBrands(brandsWithNoBrand.slice(0, 10));
      setHasFetched(true);
    } catch (error) {
      console.error("Error fetching initial brands:", error);
      // Even if API fails, set "No Brand" as an option
      const noBrandOnly = addNoBrandOption([]);
      setAllBrands(noBrandOnly);
      setFilteredBrands(noBrandOnly);
    } finally {
      setLoading(false);
    }
  };

  const searchBrands = async (searchTerm) => {
    if (!searchTerm.trim()) {
      // If search term is empty, show initial brands with "No Brand"
      setFilteredBrands(allBrands.slice(0, 10));
      return;
    }

    // Abort previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    try {
      const response = await getBrands(
        {
          name: searchTerm,
          paginate_per_page: 20
        },
        controller.signal
      );

      const brands = response?.data || [];
      
      // Add "No Brand" option only when searching for specific terms
      // but show it at the top for easy access
      let enhancedBrands = brands;
      
      // If search term is "no", "no brand", or similar, include "No Brand" option
      const searchLower = searchTerm.toLowerCase();
      if (searchLower.includes('no') || searchLower.includes('brand')) {
        enhancedBrands = addNoBrandOption(brands);
      } else {
        // Always include "No Brand" as the first option for user convenience
        enhancedBrands = addNoBrandOption(brands);
      }
      
      setFilteredBrands(enhancedBrands);
    } catch (error) {
      // Don't log CanceledError - it's expected behavior
      if (
        error.name === "AbortError" ||
        error.message?.includes("CanceledError") ||
        error.code === "ERR_CANCELED") {
        // Silently handle canceled requests - this is normal
        return;
      }
      console.error("Brand search failed:", error);
      // On error, show only "No Brand" option
      setFilteredBrands(addNoBrandOption([]));
    } finally {
      // Only stop loading if this request wasn't canceled
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const getTopBrands = () => {
    return allBrands.slice(0, 10);
  };

  const clearSearch = () => {
    setFilteredBrands(getTopBrands());
  };

  // Helper function to check if a brand is "No Brand"
  const isNoBrand = (brand) => {
    return brand && brand.name === 'No Brand' && brand.brand_id === null;
  };

  return {
    brandSuggestions: filteredBrands,
    allBrands,
    loading,
    searchBrands,
    clearSearch,
    hasFetched,
    isNoBrand
  };
};